import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmailLogFilters, SubscriberListParams } from "../../../admin-api/mailing";
import { adminApi } from "../../../api-client";
import type { SendPlayerEmail, SendTestEmail } from "../../../schemas/mailing";
import { queryKeys } from "../../query-keys";

export function useEmailLogs(params?: EmailLogFilters) {
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.mailing.list(params),
		queryKey: queryKeys.mailing.logs(params),
	});
}

export function useEmailLog(id: string) {
	return useQuery({
		enabled: !!id,
		queryFn: () => adminApi.mailing.get(id),
		queryKey: queryKeys.mailing.log(id),
	});
}

export function useEmailStats() {
	return useQuery({
		queryFn: () => adminApi.mailing.stats(),
		queryKey: queryKeys.mailing.stats,
	});
}

export function useNewsletterSubscribers(params?: SubscriberListParams) {
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.mailing.subscribers(params),
		queryKey: queryKeys.mailing.subscribers(params),
	});
}

export function useEmailTemplates() {
	return useQuery({
		queryFn: () => adminApi.mailing.templates(),
		queryKey: queryKeys.mailing.templates,
	});
}

export function useEmailTemplatePreview(key: string, locale: string) {
	return useQuery({
		enabled: !!key,
		queryFn: () => adminApi.mailing.preview(key, locale),
		queryKey: queryKeys.mailing.preview(key, locale),
	});
}

export function useSendTestEmail() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: SendTestEmail) => adminApi.mailing.sendTest(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.mailing.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.mailing.stats });
		},
	});
}

export function useSendPlayerEmail() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: SendPlayerEmail) => adminApi.mailing.sendToPlayer(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.mailing.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.mailing.stats });
		},
	});
}
